import React from 'react';
import styled from '@emotion/styled';
import ReportHeader from './components/Header';
import Table from 'app/components/Table';
import { getColumnDef } from './constants';
import { useNavigate } from 'react-router';
import { JOB_STATUS } from 'app/components/Status/JobStatus';
import { get } from 'lodash';
import classes from './Report.module.scss';
import { useList, usePagination } from 'app/definitions';
import { SubPOItemColumnDef } from 'app/modules/inspection/definitions/tables';
import { ToggleCollapseExpand } from 'app/modules/ui';
import debounce from 'lodash/debounce';
import cn from 'classnames';

const Container = styled.div`
  background: #fff;
  padding: 24px;
`;
const StyledTable = styled(Table)`
  margin-top: 16px;

  .ant-table-empty {
    .ant-table-cell {
      border-bottom: 0;
    }
  }
`;
const Reports = (props: {
  // poItems: Array<any[]>;
  reportId: number | string;
  status: string;
}) => {
  const { reportId } = props;
  const [hoveredRowKey, setHoveredRowKey] = React.useState<string | null>(null);
  const conlumDefs = getColumnDef();
  const navigate = useNavigate();
  // const defaultExpandKeys = props.poItems?.map((item: any) => item.id);

  const { current, pageSize, onChange } = usePagination();

  const { data: poItems, isLoading } = useList<{
    list: any[];
    pagination: {
      total: number;
    };
  }>({
    resource: 'inspections/:id/po-items',
    pagination: {
      current,
      pageSize
    },
    meta: {
      id: reportId
    },
    queryOption: {
      skip: !reportId
    }
  });

  const defaultExpandKeys = React.useMemo(() => {
    if (poItems?.list) {
      return poItems.list.map((item) => item.id);
    }
    return [];
  }, [poItems]);

  const onViewReport = () => {
    navigate(`/dashboard/inspection/job/${props.reportId}/report`);
  };

  const hideViewReport = React.useMemo(() => {
    if (!poItems?.list || poItems?.list.length === 0) {
      return true;
    }
    return [
      JOB_STATUS.pendingAssign,
      JOB_STATUS.waitingAccept,
      JOB_STATUS.scheduled,
      JOB_STATUS.scheduledReject
    ].includes(get(JOB_STATUS, props.status, ''));
  }, [props.status, poItems]);

  // Debounced function to set hovered row key
  const debouncedSetHoveredRowKey = React.useMemo(
    () =>
      debounce((rowKey: string | null) => {
        setHoveredRowKey(rowKey);
      }, 50), // 200ms debounce delay
    []
  );

  const handleMouseEnter = (rowKey: string) => {
    debouncedSetHoveredRowKey(rowKey); // Call debounced function on mouse enter
  };

  const handleMouseLeave = () => {
    debouncedSetHoveredRowKey(null); // Call debounced function on mouse leave
  };

  React.useEffect(() => {
    return () => {
      debouncedSetHoveredRowKey.cancel(); // Cleanup debounce on unmount
    };
  }, [debouncedSetHoveredRowKey]);

  return (
    <Container className={classes.report}>
      <ReportHeader
        hideViewReport={hideViewReport}
        onViewReport={onViewReport}
      ></ReportHeader>
      {defaultExpandKeys.length > 0 && (
        <StyledTable
          className={classes.table}
          scroll={{ x: '1000px' }}
          tableLayout="auto"
          rowKey={(record: any) => record.id}
          loading={isLoading}
          pagination={{
            current,
            pageSize,
            onChange,
            total: poItems?.pagination.total ?? 0
          }}
          dataSource={poItems?.list ?? []}
          columns={conlumDefs}
          onRow={(poItem) => {
            return {
              className: poItem.id === hoveredRowKey ? classes.hovered : '',
              onMouseEnter: () => {
                handleMouseEnter(poItem.id);
              },
              onMouseLeave: () => {
                handleMouseLeave();
              }
            };
          }}
          expandable={{
            expandedRowClassName: () => classes.expandedRow,
            expandedRowRender: (record) => {
              return (
                <div
                  onMouseEnter={() => {
                    handleMouseEnter(record.id);
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                  }}
                >
                  <Table
                    className={cn(classes.subTable, {
                      [classes.hovered]: hoveredRowKey === record.id
                    })}
                    pagination={false}
                    dataSource={record.inspectionSubPOItems}
                    columns={SubPOItemColumnDef()}
                  ></Table>
                </div>
              );
            },
            rowExpandable: () => true,
            expandIcon: ({ expanded, onExpand, record }) => {
              return (
                <ToggleCollapseExpand
                  isExpanded={expanded}
                  onToggle={(e) => {
                    onExpand(record, e);
                  }}
                />
              );
            }
          }}
        ></StyledTable>
      )}
    </Container>
  );
};

export default Reports;
